import { forwardRef } from 'react';
import { cn } from '@/lib/cn';

/**
 * Field height and type size, shared by every single-line control (Input, SelectTrigger,
 * DateInput, EmployeePicker) so a filter row never mixes two control sizes.
 *
 * On a fine pointer a field is 36px / 14px — the same height and type as a `md` Button,
 * which is what lets a filter row of selects, inputs and buttons read as one toolbar.
 * Before this, every field was 40px / 16px everywhere, so desktop filter rows and the
 * in-row status selects visibly out-weighed the buttons and table text beside them.
 *
 * On a COARSE pointer it stays 40px / 16px. That is not a style preference: below 16px,
 * iOS Safari zooms the viewport when the field receives focus. Keyed on the pointer
 * rather than a width breakpoint because iPads are wider than `md` and zoom just the same.
 */
export const fieldSize = 'h-9 text-body pointer-coarse:h-10 pointer-coarse:text-body-lg';

/**
 * Text input.
 *
 * Focus replaces the global outline with a border colour change plus a soft ring. That
 * is a legitimate substitution — the brand border reaches 4.88:1 against the surface,
 * comfortably past the 3:1 required for a non-text indicator — and it reads as part of
 * the control rather than drawn around it.
 */
const fieldClasses = [
  // `min-w-0`: see note above — without it a w-full input still widens its grid track.
  'w-full min-w-0 rounded-md border border-line-strong bg-surface',
  'text-fg placeholder:text-fg-subtle',
  'shadow-xs transition-[border-color,box-shadow] duration-150',
  'hover:border-fg-subtle',
  'focus-visible:border-focus focus-visible:ring-[3px] focus-visible:ring-action/15 focus-visible:outline-none',
  'disabled:cursor-not-allowed disabled:bg-subtle disabled:opacity-60',
  'aria-[invalid=true]:border-danger-fill aria-[invalid=true]:hover:border-danger-fill',
  'aria-[invalid=true]:focus-visible:border-danger-fill aria-[invalid=true]:focus-visible:ring-danger-fill/15',
];

export const Input = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, type = 'text', ...props }, ref) {
    return (
      <input
        ref={ref}
        type={type}
        className={cn(
          fieldClasses,
          fieldSize,
          'px-3 py-1.5',
          'file:border-0 file:bg-transparent file:text-body file:font-medium',
          className,
        )}
        {...props}
      />
    );
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, rows = 4, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(fieldClasses, 'text-body pointer-coarse:text-body-lg px-3 py-2', className)}
      {...props}
    />
  );
});

export { fieldClasses };
